import {
  ApplicationRef,
  DOCUMENT,
  DestroyRef,
  Service,
  computed,
  inject,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRouteSnapshot, NavigationEnd, Router } from '@angular/router';
import moment from 'moment';
import { filter } from 'rxjs/operators';
import { CONSTANTS } from '@shared/constants';

export type PrintOrientation = 'portrait' | 'landscape';

/** Route data a page uses to say how it wants to be printed. */
export interface PrintRouteData {
  printOrientation?: PrintOrientation;
}

/** Landscape: the dashboards print cards four across, which portrait squeezes to ~176px each. */
const DEFAULT_ORIENTATION: PrintOrientation = 'landscape';

/** Without one, output sits flush to the paper edge whenever a print dialog is set to no margins. */
const PAGE_MARGIN = '12mm';

/**
 * Sets the printed page box per route, and stamps when a print was asked for.
 *
 * `@page` cannot be scoped by selector, and the one mechanism that would — a named `@page` plus
 * the `page` property — forces a page break wherever the page context changes. So the rule is
 * rewritten on navigation instead, in one `<style>` appended after the app's stylesheets.
 */
@Service()
export class PrintPageService {
  private readonly document = inject(DOCUMENT);
  private readonly router = inject(Router);
  private readonly applicationRef = inject(ApplicationRef);
  private readonly destroyRef = inject(DestroyRef);

  private readonly printedAt = signal<Date | null>(null);
  private readonly printing = signal(false);

  /** A layout mounted behind this is laid out at its print width on every viewport otherwise. */
  readonly isPrinting = this.printing.asReadonly();

  /** Chrome omits its own date header when the reader's margins are set to none. */
  readonly printedAtLabel = computed<string>(() => {
    const at = this.printedAt();

    return at ? moment(at).format(CONSTANTS.DATES_FORMAT.DATE_TIME_FULL_YEAR) : '';
  });

  private styleElement: HTMLStyleElement | null = null;
  private applied: PrintOrientation | null = null;

  /** Until the first navigation the rule in `global.scss` is the page box. */
  start(): void {
    this.stampEachPrint();

    this.router.events
      .pipe(
        filter((event): event is NavigationEnd => event instanceof NavigationEnd),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(() => {
        // Reading router state off a destroyed injector throws.
        if (this.applicationRef.destroyed) {
          return;
        }

        this.apply(this.orientationFor(this.router.routerState.snapshot.root));
      });
  }

  /** `beforeprint` is the last point a render still reaches the DOM before pagination. */
  private stampEachPrint(): void {
    const render = (isPrinting: boolean) => (): void => {
      // A listener outliving its injector would tick a destroyed ApplicationRef.
      if (this.applicationRef.destroyed) {
        return;
      }

      if (isPrinting) {
        this.printedAt.set(new Date());
      }

      this.printing.set(isPrinting);
      this.applicationRef.tick();
    };

    const stamp = render(true);
    const discard = render(false);

    window.addEventListener('beforeprint', stamp);
    window.addEventListener('afterprint', discard);
    this.destroyRef.onDestroy(() => {
      window.removeEventListener('beforeprint', stamp);
      window.removeEventListener('afterprint', discard);
    });
  }

  private orientationFor(root: ActivatedRouteSnapshot): PrintOrientation {
    let route: ActivatedRouteSnapshot = root;

    while (route.firstChild) {
      route = route.firstChild;
    }

    // `paramsInheritanceStrategy: 'always'` means a parent's value reaches the leaf, so a section
    // can set this once for the pages under it.
    return (route.data as PrintRouteData).printOrientation ?? DEFAULT_ORIENTATION;
  }

  private apply(orientation: PrintOrientation): void {
    if (this.applied === orientation) {
      return;
    }

    if (!this.styleElement) {
      this.styleElement = this.document.createElement('style');
      this.styleElement.setAttribute('data-fb-print-page', '');
      this.document.head.append(this.styleElement);
    }

    this.styleElement.textContent = `@page { size: ${orientation}; margin: ${PAGE_MARGIN}; }`;
    this.applied = orientation;
  }
}
